import * as React from "react";
import { useDispatch } from "react-redux";

import apiClient from "api/axiosConfig";
import { getApiErrorStatus } from "api/apiError";
import { readStoredToken, SESSION_EXPIRED_EVENT } from "utils/session";

type PermissionEntry = {
  read?: boolean;
  write?: boolean;
  delete?: boolean;
  configure?: boolean;
  approve?: boolean;
};

export type PermissionMap = Record<string, PermissionEntry>;

export interface AuthUser {
  _id: string;
  name: string;
  email: string;
  role?: { name: string; permissions?: { resource: string }[] };
  permissions?: PermissionMap;
  hasAllAccess?: boolean;
  isActive?: boolean;
  // Own-profile fields from GET /api/profile (see api/profile.ts ProfileUser).
  userName?: string;
  phoneNumber?: string | null;
  phoneVerified?: boolean;
  pendingPhone?: {
    phoneNumber: string;
    phoneHint: string;
    change: boolean;
    expiresAt: string;
  } | null;
  profilePicIndex?: string | null;
  photo?: string | null;
  themePreference?: "light" | "dark" | null;
  createdAt?: string | null;
}

/**
 * - loading: a token exists and the profile is being fetched
 * - authenticated: token and profile are loaded
 * - anonymous: no token (or the server rejected it)
 * - error: the profile request failed for another reason (server down); the token is kept
 */
export type AuthStatus = "loading" | "authenticated" | "anonymous" | "error";

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  status: AuthStatus;
  setUser: (u: AuthUser | null) => void;
  setToken: (t: string | null) => void;
  refresh: () => Promise<void>;
  /** Replace the signed-in user with a fresh profile answer (after editing your own profile). */
  applyProfile: (profile: object) => void;
  /** Store a new session (after the WhatsApp code is verified); the profile is refreshed after. */
  signIn: (token: string, user: { name?: string } & Record<string, unknown>) => void;
  /** Clear the session everywhere: storage, context and redux. */
  signOut: () => void;
  hasPermission: (resource: string, action: keyof PermissionEntry) => boolean;
  /** Older name for `status === "loading"`. */
  loading: boolean;
  /** Older name for `signIn`. */
  completeLogin: (token: string, profile: unknown) => void;
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

type ProfileResponse = AuthUser & { roles?: Array<{ name?: string }>; permissions?: unknown };

const normalizeProfile = (profile: ProfileResponse): AuthUser => {
  const isSuper =
    profile?.role?.name?.toLowerCase().includes("super admin") ||
    profile?.roles?.some?.((r) => r?.name?.toLowerCase().includes("super admin"));
  const hasAllAccess = Boolean(profile?.hasAllAccess || isSuper);
  const rawPermissions = profile?.permissions;
  const permissions: PermissionMap | undefined = hasAllAccess
    ? { "*": { read: true, write: true, delete: true, configure: true, approve: true } }
    : rawPermissions && typeof rawPermissions === "object" && !Array.isArray(rawPermissions)
    ? (rawPermissions as PermissionMap)
    : undefined;
  return { ...profile, permissions, hasAllAccess };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [token, setTokenState] = React.useState<string | null>(readStoredToken);
  const [profileFailed, setProfileFailed] = React.useState(false);

  const setToken = React.useCallback((value: string | null) => {
    setTokenState(value);
    try {
      if (value) {
        localStorage.setItem("jwt", JSON.stringify(value));
      } else {
        localStorage.removeItem("jwt");
      }
    } catch (err) {
      console.warn("Unable to persist auth token", err);
    }
  }, []);

  const signOut = React.useCallback(() => {
    setToken(null);
    setUser(null);
    setProfileFailed(false);
    try {
      localStorage.removeItem("user");
    } catch (err) {
      console.warn("Unable to clear stored user", err);
    }
    dispatch({ type: "LOGOUT" });
  }, [dispatch, setToken]);

  const signIn = React.useCallback<AuthContextValue["signIn"]>(
    (nextToken, sessionUser) => {
      // The login answer carries the same profile shape, so the app can render straight away;
      // the token change below still refreshes it from /api/profile.
      setUser(normalizeProfile(sessionUser as unknown as ProfileResponse));
      setProfileFailed(false);
      setToken(nextToken);
      try {
        localStorage.setItem("user", JSON.stringify(sessionUser));
      } catch (err) {
        console.warn("Unable to persist user", err);
      }
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: {
          user: {
            ...sessionUser,
            permissions: Array.isArray(sessionUser.permissions) ? sessionUser.permissions : [],
            hasAllAccess: Boolean(sessionUser.hasAllAccess),
          },
          token: nextToken,
        },
      });
    },
    [dispatch, setToken]
  );

  const applyProfile = React.useCallback((profile: object) => {
    setUser(normalizeProfile(profile as ProfileResponse));
  }, []);

  const refresh = React.useCallback(async () => {
    if (!token) {
      return;
    }
    setProfileFailed(false);
    try {
      const res = await apiClient.get<ProfileResponse>("/api/profile");
      setUser(normalizeProfile(res.data));
    } catch (err) {
      const status = getApiErrorStatus(err);
      if (status === 401 || status === 403) {
        signOut();
        return;
      }
      console.error("Failed to load the signed-in profile", err);
      setProfileFailed(true);
    }
  }, [token, signOut]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // api/axiosConfig announces a rejected token; drop the session so guards send the user to sign in.
  React.useEffect(() => {
    const onExpired = () => signOut();
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [signOut]);

  const hasPermission = React.useCallback(
    (resource: string, action: keyof PermissionEntry) => {
      if (!user) return false;
      if (user.hasAllAccess) return true;
      if (!user.permissions) return false;
      return (
        Boolean(user.permissions[resource]?.[action]) || Boolean(user.permissions["*"]?.[action])
      );
    },
    [user]
  );

  const status: AuthStatus = !token
    ? "anonymous"
    : user
    ? "authenticated"
    : profileFailed
    ? "error"
    : "loading";

  const completeLogin = React.useCallback(
    (nextToken: string, profile: unknown) =>
      signIn(nextToken, (profile || {}) as Record<string, unknown>),
    [signIn]
  );

  const value = React.useMemo(
    () => ({
      user,
      token,
      status,
      loading: status === "loading",
      setUser,
      setToken,
      refresh,
      applyProfile,
      signIn,
      signOut,
      completeLogin,
      hasPermission,
    }),
    [
      user,
      token,
      status,
      setToken,
      refresh,
      applyProfile,
      signIn,
      signOut,
      completeLogin,
      hasPermission,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
